/**
 * Pure price-comparison and cost-aggregation rules for flight/room options.
 *
 * Shared (not re-derived) so web and native render identical price deltas and totals.
 * No React / Firebase imports — React-Native / Metro-bundlable per the `@elara/domain`
 * portability guard.
 */

import type { Cost } from './models/schemas/shared-schemas.js';

export interface PriceDelta {
  amount: number;
  percent: number;
  direction: 'up' | 'down' | 'same';
}

/**
 * Returns the original cost from a `priceHistory` array.
 *
 * Convention: the history is ordered newest-first, so the **last** entry is the oldest —
 * i.e. the price first seen, which we treat as the "original" for delta display.
 */
export function getOriginalCost(priceHistory: Array<{ cost: Cost }> | undefined): Cost | undefined {
  if (!priceHistory || priceHistory.length === 0) return undefined;
  return priceHistory.at(-1)!.cost;
}

/**
 * Computes the price delta between the current and original cost.
 *
 * Returns `null` (no comparable delta) when either cost is missing, the currencies differ
 * (a cross-currency subtraction would be meaningless), or the original amount is `0` (the
 * percentage would divide by zero). `amount` and `percent` are always non-negative; the sign
 * is carried by `direction`.
 */
export function getPriceDelta(currentCost: Cost | undefined, originalCost: Cost | undefined): PriceDelta | null {
  if (!currentCost || !originalCost) return null;
  if (currentCost.currency !== originalCost.currency) return null;
  if (originalCost.amount === 0) return null;

  const diff = currentCost.amount - originalCost.amount;
  if (diff === 0) return { amount: 0, percent: 0, direction: 'same' };

  return {
    amount: Math.abs(diff),
    percent: Math.abs((diff / originalCost.amount) * 100),
    direction: diff > 0 ? 'up' : 'down',
  };
}

/**
 * Sums item costs grouped by currency. Items with no cost or a zero amount are skipped;
 * a missing currency defaults to `'USD'`. Currencies are never combined — a mixed-currency
 * list yields one total per currency so the UI can render each separately.
 */
export function calculateTotalsByCurrency(
  items: Array<{ cost?: { amount: number; currency?: string } | null }>,
): Record<string, number> {
  return items.reduce(
    (acc, item) => {
      if (!item.cost || item.cost.amount === 0) return acc;
      const currency = item.cost.currency ?? 'USD';
      acc[currency] = (acc[currency] ?? 0) + item.cost.amount;
      return acc;
    },
    {} as Record<string, number>,
  );
}
